import { cn } from "@/lib/utils";
import { IconButton } from "components/core/buttons/IconButton";
import { Checkbox } from "components/core/inputs/CheckBox";
import {
    ButtonHTMLAttributes,
    ComponentProps,
    ReactNode,
    forwardRef,
    useRef
} from "react";

import { IconBox } from "./IconBox";
import { Menu } from "./Menu";
import {
    MENU_ITEM_INDENT,
    MENU_ITEM_PADDING,
    useMenuContext
} from "./MenuContext";

type MenuItemProps = {
    children: ReactNode;
    color?: "primary" | "error";
    space?: number;
    size?: ComponentProps<typeof Menu>["size"];
    startItem?: ReactNode;
    endItem?: ReactNode;
    selected?: boolean;
    interaction?: "hover" | "select";
    value?: string | number;
    subText?: string;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children">;

const Component = forwardRef<HTMLButtonElement, MenuItemProps>(
    (
        {
            children,
            color = "primary",
            space = 0,
            size: menuItemSize,
            startItem,
            endItem,
            interaction,
            selected,
            value,
            subText,
            ...props
        },
        ref
    ) => {
        const mainTextRef = useRef<HTMLDivElement>(null);

        const { size: menuSize } = useMenuContext();
        const size = menuItemSize || menuSize;
        const padding = MENU_ITEM_PADDING[size];

        return (
            <button
                className={cn(
                    "w-full cursor-pointer text-left",
                    color === "error" && "[&_*]:!text-error-main",
                    "hover:bg-primary-hover",
                    "[&[data-interaction=hover]]:bg-primary-hover",
                    "[&[data-interaction=select]]:bg-primary-selected",
                    "[&[data-selected=true]]:bg-primary-selected",
                    "disabled:cursor-not-allowed disabled:bg-transparent disabled:text-grey-800 disabled:opacity-50"
                )}
                style={{
                    paddingTop: `${padding.vertical}px`,
                    paddingBottom: `${padding.vertical}px`,
                    paddingLeft: `${padding.horizontal * 2 + MENU_ITEM_INDENT * space}px`,
                    paddingRight: `${padding.horizontal * 2}px`
                }}
                type="button"
                value={value}
                data-interaction={interaction}
                data-selected={selected}
                {...props}
            >
                <div className="flex w-full items-center gap-2">
                    {startItem}
                    <div
                        ref={mainTextRef}
                        className="text-body3 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-text-primary"
                    >
                        {children}
                    </div>
                    {subText && (
                        <div className="text-caption2 min-w-fit text-text-secondary">
                            {subText}
                        </div>
                    )}
                    {endItem}
                </div>
            </button>
        );
    }
);

function MenuItemIconBox(props: Omit<ComponentProps<typeof IconBox>, "size">) {
    return (
        <div className="flex h-6 w-6 items-center justify-center">
            <IconBox size="medium" {...props} />
        </div>
    );
}

function MenuItemIconButton(
    props: Omit<ComponentProps<typeof IconButton>, "size">
) {
    return <IconButton className="p-0.5" size="small" {...props} />;
}

function MenuItemCheckbox(props: ComponentProps<typeof Checkbox>) {
    return (
        <div className="flex h-6 w-6 items-center justify-center">
            <Checkbox size="medium" {...props} />
        </div>
    );
}

const MenuItem = Object.assign(Component, {
    IconBox: MenuItemIconBox,
    IconButton: MenuItemIconButton,
    Checkbox: MenuItemCheckbox
});

Component.displayName = "Component";

export { MenuItem };
